import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';
import qs from 'qs';
import { Button } from 'reactstrap';
import { useToggle } from 'react-use';

import env from '../../env';
import { malls, } from '../../shared/config';
import { fields } from '../../shared/models/shop';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import ModelFormModal from '../modals/ModelFormModal';
import Page from '../hocs/Page';
import AddButton from '../AddButton';
import SettingsTabs from '../SettingsTabs';

const shopifyInstallFields = {
  shopifyShop: {
    type: 'string',
    label: 'Shopify Shop ドメイン',
    placeholder: 'xxx.myshopify.com',
    hint: 'インストールが完了すると店舗が自動で登録されます。',
    validations: {
      myshopifyDomain: v => /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/.test(v ?? ''),
    },
  },
};

export default Page(function SettingsShops (props) {
  const { tenant, location, history } = props;
  const shopsRef = tenant.ref.collection('shops');
  const shops = useCollectionSubscription(shopsRef.orderBy('createdAt'), [tenant.id]);
  const senders = useCollectionSubscription(tenant.ref.collection('senders'), [tenant.id]);

  // Shopify App Store からインストールした直後、authShopifyCallback がこの画面へ
  // ?shopifyShop=xxx.myshopify.com を付けて返す（logisp 側に該当店舗が無かった場合）。
  // トークンは保留済みなので、着地時点でこのドメインの店舗を自動登録すれば onWriteShop が連携を完了する。
  const { shopifyShop: pendingShopifyShop } = qs.parse(location?.search ?? '', { ignoreQueryPrefix: true });
  const [showsShopifyModal, toggleShopifyModal] = useToggle(false);
  const isAlreadyRegistered = shops.some(_ => _.shopifyShop === pendingShopifyShop);

  const isAutoRegistering = useRef(false);
  useEffect(() => {
    if (isEmpty(pendingShopifyShop) || isAutoRegistering.current) return;

    isAutoRegistering.current = true;
    (async () => {
      const { docs: [registered] } = await shopsRef.where('shopifyShop', '==', pendingShopifyShop).limit(1).get();
      const shopRef = registered?.ref ?? shopsRef.doc();
      if (registered == null) {
        await shopRef.set({ mall: 'shopify', name: pendingShopifyShop, shopifyShop: pendingShopifyShop, isStockSyncEnabled: true, isOrderSyncEnabled: true, createdAt: new Date(), });
      }
      history.replace(`/settings/shops/${shopRef.id}`);
    })();
  }, [pendingShopifyShop]);

  return (
    <div className="settings-shops container-fluid">
      <div className="p-4 bg-white my-4">
        <SettingsTabs activeTab="shops" />
        {
          pendingShopifyShop != null && !isAlreadyRegistered && (
            <div className="alert alert-info">
              <span className="fas fa-spinner fa-spin mr-2" />
              Shopifyストア「{pendingShopifyShop}」を店舗として登録しています...
            </div>
          )
        }
        <div className="d-flex justify-content-start mb-3">
          <AddButton itemRef={shopsRef.doc()} FormModal={ModelFormModal} formProps={{ title: '店舗 追加', fields: fields({ tenant, senders }), }} />
          {
            tenant.tenantOptionValues?.includes('shopify') && (
              <Button color="primary" className="ml-2" onClick={_ => toggleShopifyModal(true)}>
                <span className="fab fa-shopify mr-1" />
                Shopifyストアを連携
              </Button>
            )
          }
          {
            showsShopifyModal && (
              <ModelFormModal
                isOpen
                title="Shopifyストアを連携"
                fields={shopifyInstallFields}
                submitLabel="Shopifyでインストール"
                onClickClose={_ => toggleShopifyModal(false)}
                onSubmit={({ shopifyShop }) => { window.location.href = `${env('CLOUD_FUNCTIONS_ENDPOINT')}/authShopify?shop=${encodeURIComponent(shopifyShop)}`; }}
              />
            )
          }
        </div>
        <div className="mt-3">
          {
            shops.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {
                  shops.map((shop) => {
                    const { id, mall, name } = shop;

                    return (
                      <Link key={id} to={`/settings/shops/${id}`} className="card shadow-sm text-decoration-none text-body">
                        <div className="card-body d-flex justify-content-between align-items-center">
                          <div>
                            <span className="badge badge-secondary mr-2">{malls[mall]?.label}</span>
                            <span className="font-weight-bold">{name}</span>
                          </div>
                          <span className="fas fa-chevron-right text-muted" />
                        </div>
                      </Link>
                    );
                  })
                }
              </div>
            ) : (
              <div>
                店舗は未登録です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
